import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { selfServiceHomePath, useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  getMyPayslips,
  downloadPayslip
} from '../../services/api/implementation/impl';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * An employee's own pay, month by month.
 *
 * Deliberately separate from Payroll: that screen is a payroll officer running
 * a month for everybody, this one is a person looking at their own slip and
 * needs no payroll permission at all. Only released months appear — the server
 * withholds drafts, so nobody sees a figure that is still being corrected.
 */
const MyPayslips = () => {
  const { onBack } = useSmartBack({ fallback: selfServiceHomePath() });
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const payslips = useQuery({
    queryKey: ['myPayslips'],
    queryFn: getMyPayslips
  });

  const rows = payslips.data ?? [];

  const onDownload = async (row) => {
    setDownloading(row.id);
    try {
      await downloadPayslip(row.id, `payslip-${fileLabel(row.period)}.pdf`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Could not download the payslip'
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'My payslips' }} />

      <div className="p-4 space-y-3">
        {payslips.isFetching && rows.length === 0 ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 py-10 text-center">
            <FileText size={36} className="mx-auto text-gray-300" />
            <p className="mt-3 text-sm text-gray-500 max-w-md mx-auto">
              No payslips yet. A month appears here once payroll has been run
              and released by your company.
            </p>
          </div>
        ) : (
          rows.map((row) => (
            <PayslipCard
              key={row.id}
              row={row}
              open={expanded === row.id}
              onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
              onDownload={() => onDownload(row)}
              downloading={downloading === row.id}
            />
          ))
        )}
      </div>
    </Layout>
  );
};

const PayslipCard = ({ row, open, onToggle, onDownload, downloading }) => {
  const earnings = row.earnings ?? [];
  const employer = row.employer_contributions ?? [];
  const tds = num(row.tds);
  const deductions =
    tds > 0
      ? [
          ...(row.deductions ?? []),
          { code: 'TDS', name: 'Income tax (TDS)', amount: tds }
        ]
      : row.deductions ?? [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">
              {monthLabel(row.period)}
            </span>
            {row.period?.status === 'PAID' && (
              <span className="px-2 py-0.5 rounded text-xs bg-success-bg text-success-fg">
                PAID
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {days(row.payable_days)} paid of {days(row.total_working_days)} days
            {num(row.lop_days) > 0 ? ` · ${days(row.lop_days)} LOP` : ''}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-gray-500">Net pay</div>
            <div className="text-lg font-semibold text-brand-700">
              {money(row.net)}
            </div>
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            {open ? 'Hide' : 'Breakdown'}
          </button>
          <button
            type="button"
            disabled={downloading}
            onClick={onDownload}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            <Download size={15} />
            {downloading ? 'Preparing…' : 'PDF'}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <LineTable title="Earnings" lines={earnings} />
            <Total label="Gross" amount={row.gross} />
          </div>

          <div>
            <LineTable title="Deductions" lines={deductions} />
            <Total label="Total deductions" amount={row.total_deductions} />
            <Total label="Net pay" amount={row.net} emphasis />
          </div>

          {employer.length > 0 && (
            <div className="md:col-span-2">
              <LineTable title="Employer contributions" lines={employer} />
              <p className="text-xs text-gray-500 mt-1">
                Paid by the company on top of your salary. Not deducted from
                your pay.
              </p>
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap gap-4 text-xs text-gray-600 border-t border-gray-100 pt-3">
            <span>
              Present <b className="text-gray-900">{days(row.present_days)}</b>
            </span>
            <span>
              Paid leave{' '}
              <b className="text-gray-900">{days(row.paid_leave_days)}</b>
            </span>
            <span>
              Loss of pay <b className="text-gray-900">{days(row.lop_days)}</b>
            </span>
            {!!row.remarks && <span className="italic">{row.remarks}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const LineTable = ({ title, lines }) => {
  if (!lines.length) return null;
  return (
    <>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        {title}
      </div>
      <div className="space-y-1.5">
        {lines.map((line, index) => (
          <div
            key={`${line.code || line.name}-${index}`}
            className="flex justify-between items-start text-sm"
          >
            <div className="pr-3">
              <div className="text-gray-800">{line.name || line.code}</div>
              {!!line.note && (
                <div className="text-xs text-gray-400">{line.note}</div>
              )}
            </div>
            <div className="text-gray-900 font-medium whitespace-nowrap">
              {money(line.amount)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const Total = ({ label, amount, emphasis }) => (
  <div className="flex justify-between text-sm border-t border-gray-100 mt-2 pt-2">
    <span className="font-semibold text-gray-800">{label}</span>
    <span
      className={`font-semibold ${emphasis ? 'text-brand-700' : 'text-gray-900'}`}
    >
      {money(amount)}
    </span>
  </div>
);

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) =>
  `₹${num(value).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;

const days = (value) => {
  const parsed = num(value);
  return Number.isInteger(parsed) ? `${parsed}` : parsed.toFixed(1);
};

const monthLabel = (period) =>
  period ? `${MONTHS[(period.month ?? 1) - 1] ?? ''} ${period.year}` : 'Payslip';

const fileLabel = (period) =>
  period ? `${MONTHS[(period.month ?? 1) - 1] ?? ''}-${period.year}` : 'payslip';

export default MyPayslips;
